(() => {
  if (customElements.get('finewood-mega-menu')) return;

  class FinewoodMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.dialog = this.querySelector('dialog');
      this.tabs = [...this.querySelectorAll('[data-fw-tab]')];
      this.panels = [...this.querySelectorAll('[data-fw-panel]')];
      if (!this.dialog || !this.tabs.length || !this.dialog.showModal) return;
      this.controller = new AbortController();
      this.selector = this.dataset.triggerSelector || '[data-finewood-shop-trigger]';
      this.media = matchMedia('(max-width: 991px)');
      this.isOpen = false;
      this.trigger = null;
      this.triggerStates = new Map();
      this.listen = (node, event, fn, options = {}) => node.addEventListener(event, fn, { ...options, signal: this.controller.signal });
      this.refreshTriggers();
      this.configureAccordion();
      this.listen(document, 'click', (event) => this.onDocumentClick(event), { capture: true });
      this.listen(document, 'keydown', (event) => this.onDocumentKeydown(event), { capture: true });
      this.listen(document, 'pointerover', (event) => this.onPointerOver(event));
      this.listen(document, 'pointermove', (event) => this.onPointerMove(event), { passive: true });
      this.listen(this.dialog, 'click', (event) => this.onDialogClick(event));
      this.listen(this.dialog, 'close', () => { if (!this.dialog.open) this.afterClose(); });
      this.listen(this.dialog, 'cancel', (event) => { event.preventDefault(); this.close(); });
      this.listen(this.querySelector('[data-fw-close]'), 'click', () => this.close());
      this.listen(this.querySelector('[role="tablist"]'), 'keydown', (event) => this.onTabKeydown(event));
      this.tabs.forEach(tab => {
        this.listen(tab, 'click', () => this.selectTab(tab.dataset.fwTab));
        this.listen(tab, 'pointerenter', (event) => { if (!this.media.matches && event.pointerType === 'mouse') this.selectTab(tab.dataset.fwTab); });
      });
      this.querySelectorAll('[data-fw-accordion]').forEach(button => this.listen(button, 'click', () => {
        if (!this.media.matches) return;
        this.setAccordion(button, button.getAttribute('aria-expanded') !== 'true');
      }));
      this.listen(this.media, 'change', () => { this.configureAccordion(); if (this.isOpen) this.position(); });
      this.listen(window, 'resize', () => this.position(), { passive: true });
      this.listen(window, 'scroll', () => this.position(), { passive: true });
      this.listen(document, 'shopify:section:load', () => this.refreshTriggers());
      this.listen(document, 'shopify:section:select', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId && (this.dataset.editorScope !== 'header' || this.dataset.editorPreview === 'true')) this.open();
      });
      this.listen(document, 'shopify:section:deselect', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close();
      });
      this.listen(document, 'shopify:block:select', (event) => {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        if (!this.tabs.some(tab => tab.dataset.fwTab === event.detail.blockId)) return;
        this.open(); this.selectTab(event.detail.blockId);
      });
      this.listen(document, 'finewood:menu:open', (event) => {
        this.open(event.detail?.trigger);
        if (event.detail?.category) this.selectTab(event.detail.category);
      });
      if (this.dataset.editorPreview === 'true') requestAnimationFrame(() => this.open());
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this.close(false);
      this.controller?.abort();
      clearTimeout(this.closeTimer);
      this.resizeObserver?.disconnect();
      this.triggerStates?.forEach((attributes, trigger) => {
        Object.entries(attributes).forEach(([name, value]) => value === null ? trigger.removeAttribute(name) : trigger.setAttribute(name, value));
      });
      this._connected = false;
    }

    findTriggers() {
      try { return [...document.querySelectorAll(this.selector)]; }
      catch { console.warn('FineWood menu: check the trigger selector in the section settings.'); return []; }
    }

    refreshTriggers() {
      this.findTriggers().forEach(trigger => {
        if (!this.triggerStates.has(trigger)) {
          this.triggerStates.set(trigger, Object.fromEntries(['aria-controls', 'aria-expanded', 'aria-haspopup', 'role'].map(name => [name, trigger.getAttribute(name)])));
        }
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', String(this.isOpen));
        trigger.setAttribute('aria-haspopup', 'dialog');
        if (trigger.tagName === 'A') trigger.setAttribute('role', 'button');
      });
    }

    closestTrigger(target) {
      try { return target instanceof Element ? target.closest(this.selector) : null; }
      catch { return null; }
    }

    onDocumentClick(event) {
      const trigger = this.closestTrigger(event.target);
      if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.isOpen ? this.close() : this.open(trigger);
    }

    onDocumentKeydown(event) {
      const trigger = this.closestTrigger(event.target);
      if (!trigger || (event.key !== 'ArrowDown' && !(event.key === ' ' && trigger.tagName === 'A'))) return;
      event.preventDefault(); event.stopImmediatePropagation(); this.open(trigger);
    }

    onPointerOver(event) {
      if (this.dataset.hover !== 'true' || this.media.matches || event.pointerType !== 'mouse') return;
      const trigger = this.closestTrigger(event.target);
      if (trigger && !this.isOpen) this.open(trigger);
    }

    onPointerMove(event) {
      if (!this.isOpen || this.dataset.hover !== 'true' || this.media.matches || event.pointerType !== 'mouse') return;
      const inside = element => {
        if (!element) return false;
        const box = element.getBoundingClientRect();
        return event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top - 6 && event.clientY <= box.bottom + 6;
      };
      if (inside(this.dialog) || inside(this.trigger)) { clearTimeout(this.closeTimer); this.closeTimer = null; }
      else if (!this.closeTimer) this.closeTimer = setTimeout(() => { this.closeTimer = null; this.close(false); }, 220);
    }

    onDialogClick(event) {
      if (event.target === this.dialog) {
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      }
      if (event.target.closest('a[href]')) this.close(false);
    }

    async open(trigger) {
      if (this.isOpen || this.opening) return;
      this.opening = true;
      try {
        this.trigger = trigger || this.findTriggers().find(node => node.getClientRects().length) || null;
        this.returnFocusTarget = null;
        if (!this.dispatchEvent(new CustomEvent('finewood:before-open', { bubbles: true, cancelable: true, detail: { trigger: this.trigger } }))) return;
        this.returnFocusTarget = await this.closeOriginDrawer(this.trigger);
        if (!this.isConnected) return;
        this.previousFocus = document.activeElement;
        this.position();
        this.savedOverflow = document.documentElement.style.overflow;
        this.savedScrollbarGutter = document.documentElement.style.scrollbarGutter;
        document.documentElement.style.scrollbarGutter = 'stable';
        document.documentElement.style.overflow = 'hidden';
        try { this.dialog.showModal(); }
        catch { this.restoreScroll(); return; }
        this.isOpen = true;
        this.refreshTriggers();
        this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus({ preventScroll: true });
        const header = this.headerElement();
        if (header && window.ResizeObserver) {
          this.resizeObserver?.disconnect();
          this.resizeObserver = new ResizeObserver(() => this.position());
          this.resizeObserver.observe(header);
        }
      } finally {
        this.opening = false;
      }
    }

    async closeOriginDrawer(trigger) {
      const drawer = trigger?.closest('header-drawer');
      if (!drawer) return null;
      const root = drawer.querySelector(':scope > details') || drawer.querySelector('details.menu-drawer-container');
      if (!root?.open) return null;
      const summary = root.querySelector(':scope > summary');
      // Reuse this Dawn-derived theme's drawer API when it is available.
      if (typeof drawer.closeMenuDrawer === 'function') {
        try { drawer.closeMenuDrawer(new Event('click'), summary); }
        catch { /* Fall through to the supplied header's exact DOM structure. */ }
      }
      root.querySelectorAll('details[open]').forEach(details => { details.open = false; details.classList.remove('menu-opening'); });
      root.querySelectorAll('summary[aria-expanded="true"]').forEach(node => node.setAttribute('aria-expanded', 'false'));
      root.querySelectorAll('.submenu-open').forEach(node => node.classList.remove('submenu-open'));
      root.classList.remove('menu-opening');
      root.open = false;
      summary?.setAttribute('aria-expanded', 'false');
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(summary);
      const breakpoint = drawer.dataset.breakpoint || 'tablet';
      document.body.classList.remove(`overflow-hidden-${breakpoint}`, 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop', 'overflow-hidden');
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      return summary;
    }

    close(returnFocus = true) {
      if (!this.isOpen) return;
      this.returnFocus = returnFocus;
      this.dialog.close();
      this.afterClose();
    }

    restoreScroll() {
      if (this.savedOverflow !== undefined) document.documentElement.style.overflow = this.savedOverflow;
      if (this.savedScrollbarGutter !== undefined) document.documentElement.style.scrollbarGutter = this.savedScrollbarGutter;
    }

    afterClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      clearTimeout(this.closeTimer); this.closeTimer = null;
      this.resizeObserver?.disconnect();
      this.restoreScroll(); this.refreshTriggers();
      if (this.returnFocus !== false) {
        const target = this.returnFocusTarget?.isConnected ? this.returnFocusTarget : this.trigger?.isConnected ? this.trigger : this.previousFocus;
        target?.focus?.({ preventScroll: true });
      }
      this.returnFocus = true;
    }

    headerElement() {
      const selector = this.dataset.headerSelector;
      if (selector) { try { return document.querySelector(selector); } catch { return null; } }
      return this.trigger?.closest('header') || document.querySelector('sticky-header, .header-wrapper, header');
    }

    position() {
      if (!this.dialog) return;
      const header = this.headerElement();
      const bottom = header?.getBoundingClientRect().bottom ?? this.trigger?.getBoundingClientRect().bottom ?? 0;
      const top = Math.max(0, Math.min(bottom, innerHeight - 180));
      this.style.setProperty('--fw-mm-top', `${Math.round(top)}px`);
    }

    selectTab(id, focus = false) {
      if (!this.tabs.some(tab => tab.dataset.fwTab === id)) return;
      this.tabs.forEach(tab => {
        const active = tab.dataset.fwTab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus({ preventScroll: true });
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.fwPanel !== id; });
      this.querySelector('.fw-mm__panels').scrollTop = 0;
    }

    onTabKeydown(event) {
      const vertical = !this.media.matches;
      const previous = vertical ? 'ArrowUp' : 'ArrowLeft';
      const next = vertical ? 'ArrowDown' : 'ArrowRight';
      if (![previous, next, 'Home', 'End'].includes(event.key)) return;
      const index = this.tabs.indexOf(event.target.closest('[data-fw-tab]'));
      if (index < 0) return;
      event.preventDefault();
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === next ? 1 : -1) + this.tabs.length) % this.tabs.length;
      this.selectTab(this.tabs[target].dataset.fwTab, true);
    }

    setAccordion(button, expanded) {
      const body = document.getElementById(button.getAttribute('aria-controls'));
      if (!body || !this.contains(body)) return;
      button.setAttribute('aria-expanded', String(expanded)); body.hidden = !expanded;
    }

    configureAccordion() {
      this.querySelector('[role="tablist"]').setAttribute('aria-orientation', this.media.matches ? 'horizontal' : 'vertical');
      this.querySelectorAll('[data-fw-accordion]').forEach(button => {
        button.disabled = !this.media.matches;
        // Keep every column visible on desktop; mobile starts with By edge open.
        this.setAccordion(button, !this.media.matches || button.closest('[data-fw-group]').dataset.fwGroup === '1');
      });
    }
  }
  customElements.define('finewood-mega-menu', FinewoodMegaMenu);
})();
